import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import queryString from "query-string";
import {Box, Chip, Divider, IconButton, Skeleton, Tooltip, Typography} from "@mui/material";
import {ChevronLeft, ChevronRight, CloseOutlined, ControlCamera, OpenInNew, StorefrontOutlined} from "@mui/icons-material";
import DraggablePopover from "../index";
import Button from "../../../base/button/button";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import DateUtils from "../../../../../core/services/date-utils";
import {formatMoney} from "../../../../../core/services/utils/utils";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import {CompetitorPriceReportsDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import {
    buildCompetitorPriceReportsListRequest,
    defaultCompetitorPriceReportsOrderBy,
} from "../../../../views/panel/operator/lookup-v2/competitor-price-report/build-list-request";

const prefetchPageSize = 50;
const compactDateTimeFormat = 'MMM D, h:mm A';
const cancelSelector = '.draggable-popover-cancel';

const triggerButtonProps = {
    sx: {
        minWidth: 0,
        p: 0.5,
        lineHeight: 1,
        color: 'inherit',
    },
};

const hiddenTriggerIconProps = {
    sx: {display: 'none'},
};

/**
 * Milliseconds for a report submit datetime, used for newest-first sorting.
 */
const getSubmitTime = (report) => {
    if (!report?.submitDateTime)
        return 0;
    const parsed = DateUtils.serverToLocal(report.submitDateTime);
    return parsed?.valueOf?.() ?? 0;
};

/**
 * Groups reports by competitor, then by identity for the current popover mode.
 */
const groupReports = (reports, isOperatorMode, unknownCompetitor) => {
    const competitorMap = new Map();
    reports.forEach((report) => {
        const competitorKey = String(report.competitorId ?? report.competitorName ?? '');
        if (!competitorMap.has(competitorKey)) {
            competitorMap.set(competitorKey, {
                key: competitorKey,
                name: report.competitorName || unknownCompetitor,
                identities: new Map(),
            });
        }
        const competitor = competitorMap.get(competitorKey);
        const identityKey = isOperatorMode
            ? [report.partId ?? report.partNumber, report.customerId ?? report.customerName, report.priceLevelId ?? report.priceLevelTitle].join('|')
            : String(report.customerId ?? report.customerName ?? '');
        if (!competitor.identities.has(identityKey)) {
            competitor.identities.set(identityKey, {
                key: identityKey,
                partNumber: report.partNumber,
                customerName: report.customerName,
                priceLevelTitle: report.priceLevelTitle,
                reports: [],
            });
        }
        competitor.identities.get(identityKey).reports.push(report);
    });

    return Array.from(competitorMap.values()).map((competitor) => {
        const identities = Array.from(competitor.identities.values()).map((identity) => ({
            ...identity,
            reports: [...identity.reports].sort((left, right) => getSubmitTime(right) - getSubmitTime(left)),
        }));
        identities.sort((left, right) => getSubmitTime(right.reports[0]) - getSubmitTime(left.reports[0]));
        return {
            key: competitor.key,
            name: competitor.name,
            reportCount: identities.reduce((sum, identity) => sum + identity.reports.length, 0),
            identities,
            latestTime: getSubmitTime(identities[0]?.reports[0]),
        };
    }).sort((left, right) => right.latestTime - left.latestTime);
};

/**
 * Builds the competitor-reports page URL with filters that useSearch hydrates on load.
 */
const buildReportsPageUrl = ({partId, partNumber, priceLevelId, operatorId, operatorName, mineOnly}) => {
    const filters = operatorId
        ? {operator: {id: operatorId, firstName: operatorName || '', lastName: ''}}
        : {
            part: {id: partId, partNo: partNumber || ''},
            priceLevelId,
        };
    const search = queryString.stringify({
        ...(mineOnly ? {tab: 'mine'} : {}),
        filters: JSON.stringify(filters),
        orderBy: JSON.stringify({
            field: CompetitorPriceReportsDataGridColumnNames.submitDateTime,
            descending: true,
        }),
    }, {arrayFormat: 'bracket'});
    return `${routes.lookup.competitorPriceReport}?${search}`;
};

/**
 * Draggable, non-modal competitor price reports for a locked product + price level, or submitted-by operator.
 */
const CompetitorPriceReportsPopover = ({
                                           children,
                                           partId,
                                           partNumber,
                                           priceLevelId,
                                           priceLevelTitle,
                                           operatorId,
                                           operatorName,
                                           mineOnly = false,
                                           wrapperProps,
                                           buttonProps,
                                           iconProps,
                                       }) => {
    const isOperatorMode = !!operatorId;
    const canRenderPopover = isOperatorMode || (!!partId && !!priceLevelId);
    const width = isOperatorMode ? 720 : 560;
    const height = typeof window === 'undefined' ? 720 : Math.round(window.innerHeight * 0.8);
    const popupId = isOperatorMode
        ? `competitor-price-reports-operator-${operatorId}`
        : `competitor-price-reports-part-${partId}-${priceLevelId}`;

    return (
        <DraggablePopover
            popupId={popupId}
            canRenderPopover={canRenderPopover}
            wrapperProps={wrapperProps}
            buttonProps={{...triggerButtonProps, ...buttonProps}}
            iconProps={{...hiddenTriggerIconProps, ...iconProps}}
            width={width}
            height={height}
            popoverElement={
                <CompetitorPriceReportsPopoverContent
                    partId={partId}
                    partNumber={partNumber}
                    priceLevelId={priceLevelId}
                    priceLevelTitle={priceLevelTitle}
                    operatorId={operatorId}
                    operatorName={operatorName}
                    mineOnly={mineOnly}
                />
            }
            draggableProps={{
                cancel: cancelSelector,
            }}
        >
            {children}
        </DraggablePopover>
    );
};

/**
 * Popover body: grab header, latest 50 reports grouped by competitor, link to the full list.
 */
const CompetitorPriceReportsPopoverContent = ({
                                                  close,
                                                  width,
                                                  height,
                                                  partId,
                                                  partNumber,
                                                  priceLevelId,
                                                  priceLevelTitle,
                                                  operatorId,
                                                  operatorName,
                                                  mineOnly = false,
                                              }) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.competitor-price-reports-popover'});
    const isMounted = useIsMounted();
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const isOperatorMode = !!operatorId;
    const isProductMode = !isOperatorMode && !!partId && !!priceLevelId;

    const lockedCriteria = useMemo(() => ({
        partId: partId ? Number(partId) : undefined,
        priceLevelId: priceLevelId ? Number(priceLevelId) : undefined,
        operatorId: operatorId ?? undefined,
    }), [operatorId, partId, priceLevelId]);

    /**
     * Loads the latest page of matching reports (newest first).
     */
    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            const forApi = buildCompetitorPriceReportsListRequest({
                paginationInfo: {currentPage: 1, pageSize: prefetchPageSize},
                orderBy: defaultCompetitorPriceReportsOrderBy,
                filters: {},
            }, lockedCriteria);
            const response = mineOnly
                ? await Api.listMyCompetitorPriceReports(forApi)
                : await Api.listCompetitorPriceReports(forApi);
            if (cancelled || !isMounted())
                return;
            if (response?.resultFlag) {
                setData(response.data?.items ?? []);
                setTotalCount(response.data?.totalCount ?? 0);
            } else {
                setData([]);
                setTotalCount(0);
            }
            setLoading(false);
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [isMounted, lockedCriteria, mineOnly]);

    const sections = useMemo(
        () => groupReports(data, isOperatorMode, t('unknownCompetitor')),
        [data, isOperatorMode, t],
    );

    const context = isOperatorMode
        ? (operatorName || t('operatorFallback'))
        : isProductMode
            ? `${partNumber || t('productFallback')} · ${priceLevelTitle || t('priceLevelFallback')}`
            : '';

    const title = mineOnly ? t('titleOfMine', {context}) : t('titleOf', {context});

    const emptyMessage = isOperatorMode
        ? t('emptyOperator', {operator: operatorName || t('operatorFallback')})
        : isProductMode
            ? t('emptyProduct', {
                part: partNumber || t('productFallback'),
                priceLevel: priceLevelTitle || t('priceLevelFallback'),
            })
            : t('emptyGeneric');

    const reportsPageUrl = useMemo(() => buildReportsPageUrl({
        partId,
        partNumber,
        priceLevelId,
        operatorId,
        operatorName,
        mineOnly,
    }), [mineOnly, operatorId, operatorName, partId, partNumber, priceLevelId]);

    return (
        <Box
            sx={{
                width: `min(90vw, ${width}px)`,
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1,
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    px: 1.5,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'grab',
                    '&:active': {cursor: 'grabbing'},
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                        <Tooltip title={t('moveTooltip')} placement={'right'} followCursor>
                            <ControlCamera color={'primary'} sx={{flexShrink: 0}}/>
                        </Tooltip>
                        <Tooltip title={title}>
                            <Typography variant={'subtitle2'} fontWeight={700} noWrap color={'text.primary'}>
                                {title}
                            </Typography>
                        </Tooltip>
                    </Box>
                    <Box className={'draggable-popover-cancel'} sx={{display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0}}>
                        <Button
                            variant={'text'}
                            size={'small'}
                            color={'primary'}
                            component={Link}
                            to={reportsPageUrl}
                            onClick={(e) => e.stopPropagation()}
                            sx={{display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap'}}
                        >
                            {mineOnly ? t('viewMyReports') : t('viewAllReports')}
                            <OpenInNew sx={{fontSize: 14}}/>
                        </Button>
                        <IconButton onClick={close} color={'error'} size={'small'} aria-label={t('close')}>
                            <CloseOutlined/>
                        </IconButton>
                    </Box>
                </Box>
                {
                    !loading &&
                    <Typography variant={'caption'} color={'text.secondary'} display={'block'} sx={{mt: 0.5, ml: 4}}>
                        {t('showingLatest', {
                            shown: Math.min(data.length, prefetchPageSize),
                            total: totalCount,
                            max: prefetchPageSize,
                        })}
                    </Typography>
                }
            </Box>
            <Box
                className={'draggable-popover-cancel'}
                sx={{px: 1.5, py: 1.25, overflowY: 'auto', flex: 1}}
            >
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25}}>
                    {
                        loading &&
                        Array.from({length: 3}).map((_, key) => (
                            <Skeleton
                                key={key}
                                variant={'rounded'}
                                width={'100%'}
                                height={88}
                                sx={{transform: 'none'}}
                            />
                        ))
                    }
                    {
                        !loading && sections.map((section) => (
                            <CompetitorSection
                                key={section.key}
                                section={section}
                                isOperatorMode={isOperatorMode}
                            />
                        ))
                    }
                    {
                        !loading && data.length < 1 &&
                        <Box sx={{py: 4, px: 2, textAlign: 'center'}}>
                            <StorefrontOutlined color={'disabled'} sx={{fontSize: 32, mb: 1}}/>
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {emptyMessage}
                            </Typography>
                        </Box>
                    }
                </Box>
            </Box>
        </Box>
    );
};

/**
 * One competitor block: header plus dense identity rows.
 */
const CompetitorSection = ({section, isOperatorMode}) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.competitor-price-reports-popover'});

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <StorefrontOutlined sx={{fontSize: 18, color: 'tertiary.main', flexShrink: 0}}/>
                <Tooltip title={section.name}>
                    <Typography variant={'subtitle2'} fontWeight={700} noWrap color={'text.primary'} sx={{flex: 1, minWidth: 0}}>
                        {section.name}
                    </Typography>
                </Tooltip>
                <Chip
                    size={'small'}
                    color={'primary'}
                    label={t('reportsCount', {count: section.reportCount})}
                    sx={{height: 20, '& .MuiChip-label': {px: 0.75, fontSize: 11}}}
                />
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                {
                    section.identities.map((identity, index) => (
                        <React.Fragment key={identity.key}>
                            {
                                index > 0 &&
                                <Divider/>
                            }
                            <ReportIdentityRow
                                identity={identity}
                                isOperatorMode={isOperatorMode}
                            />
                        </React.Fragment>
                    ))
                }
            </Box>
        </Box>
    );
};

/**
 * Formats a report submit datetime for the dense row.
 */
const formatSubmitDateTime = (report) => {
    if (!report?.submitDateTime)
        return '';
    return DateUtils.serverToLocal(report.submitDateTime).format(compactDateTimeFormat);
};

/**
 * Formats a report price for the dense row.
 */
const formatReportPrice = (report, emptyValue) => {
    if (report?.price == null || report.price === '')
        return emptyValue;
    return formatMoney(report.price);
};

/**
 * One identity row with a latest-first price/datetime carousel.
 */
const ReportIdentityRow = ({identity, isOperatorMode}) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.competitor-price-reports-popover'});
    const [selectedIndex, setSelectedIndex] = useState(0);
    const reports = identity.reports;
    const selectedReport = reports[selectedIndex] ?? reports[0];
    const canGoPrevious = selectedIndex > 0;
    const canGoNext = selectedIndex < reports.length - 1;

    /**
     * Shows the previous (newer) snapshot when not already on the latest.
     */
    const goPrevious = useCallback(() => {
        setSelectedIndex((current) => Math.max(0, current - 1));
    }, []);

    /**
     * Shows the next (older) snapshot when not already on the oldest.
     */
    const goNext = useCallback(() => {
        setSelectedIndex((current) => Math.min(reports.length - 1, current + 1));
    }, [reports.length]);

    const identityMeta = [identity.partNumber, identity.priceLevelTitle].filter(Boolean).join(' · ');
    const customerName = identity.customerName || t('emptyValue');
    const chevronSx = {
        p: 0.25,
        '&.Mui-disabled': {opacity: 0.35},
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 1.5,
                py: 1.25,
                color: 'text.primary',
                '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                },
            }}
        >
            <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25}}>
                {
                    isOperatorMode && !!identityMeta &&
                    <Tooltip title={identityMeta}>
                        <Typography variant={'caption'} color={'text.secondary'} noWrap>
                            {identityMeta}
                        </Typography>
                    </Tooltip>
                }
                <Tooltip title={customerName}>
                    <Typography variant={'body2'} fontWeight={500} noWrap color={'text.primary'}>
                        {customerName}
                    </Typography>
                </Tooltip>
            </Box>
            <Box
                className={'draggable-popover-cancel'}
                sx={{display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.5}}
            >
                {
                    reports.length > 1 &&
                    <IconButton
                        size={'small'}
                        color={'secondary'}
                        disabled={!canGoPrevious}
                        onClick={goPrevious}
                        aria-label={t('previousReport')}
                        sx={chevronSx}
                    >
                        <ChevronLeft sx={{fontSize: 18}}/>
                    </IconButton>
                }
                <Box sx={{minWidth: 112, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25}}>
                    <Typography
                        variant={'body2'}
                        fontWeight={700}
                        color={'text.primary'}
                        sx={{fontVariantNumeric: 'tabular-nums'}}
                    >
                        {formatReportPrice(selectedReport, t('emptyValue'))}
                    </Typography>
                    <Typography variant={'caption'} color={'text.secondary'} noWrap>
                        {formatSubmitDateTime(selectedReport) || t('emptyValue')}
                        {
                            reports.length > 1 &&
                            <>
                                {' · '}
                                {t('slideIndex', {current: selectedIndex + 1, total: reports.length})}
                            </>
                        }
                    </Typography>
                </Box>
                {
                    reports.length > 1 &&
                    <IconButton
                        size={'small'}
                        color={'secondary'}
                        disabled={!canGoNext}
                        onClick={goNext}
                        aria-label={t('nextReport')}
                        sx={chevronSx}
                    >
                        <ChevronRight sx={{fontSize: 18}}/>
                    </IconButton>
                }
            </Box>
        </Box>
    );
};

export default CompetitorPriceReportsPopover;
